import { resourcesCopy } from '@/copy/resources'
import { Card } from '@/ui/Card/Card'
import { TextareaField } from '@/ui/Field/Field'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Markdown } from '@/ui/Message/Markdown'
import { StatusBadge, type StatusTone } from '@/ui/Status/Status'
import type { FieldProps, Fields, ResourceFormDescriptor } from './shared'

/**
 * Form-state key carrying the fields this form doesn't edit (status,
 * timestamps, decision metadata) verbatim, so saving round-trips them instead
 * of clobbering them.
 */
const REST_KEY = '__rest'

interface PlanRest {
  status?: string
  submitted_at?: number
  decided_at?: number
  decided_by?: string
  annotations?: string | null
  [key: string]: unknown
}

function parseRest(raw: string | undefined): PlanRest {
  try {
    return JSON.parse(raw || '{}') as PlanRest
  } catch {
    return {}
  }
}

/**
 * Plan editor: the `plan_text` is editable (with markdown preview, like
 * notes); lifecycle state (status / decision metadata) is shown read-only —
 * it belongs to the approve/reject flow in the thread card — and preserved
 * untouched on save.
 */
// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function PlanFields({ fields, set }: FieldProps) {
  const rest = parseRest(fields[REST_KEY])
  return (
    <Stack gap="space-8">
      <StatusMeta rest={rest} />
      <TextareaField
        id="res-plan-text"
        label={resourcesCopy.forms.plan.plan}
        value={fields.plan_text ?? ''}
        onValue={(v) => set('plan_text', v)}
        textareaProps={{ rows: 8 }}
      />
      <section aria-label={resourcesCopy.form.preview}>
        <Card variant="inset">
          {fields.plan_text ? (
            <Markdown source={fields.plan_text} />
          ) : (
            <Text tone="text-mute">{resourcesCopy.form.previewEmpty}</Text>
          )}
        </Card>
      </section>
      {rest.annotations ? (
        <Text as="p" variant="caption" tone="text-mute">
          {resourcesCopy.forms.plan.reviewerFeedback(rest.annotations)}
        </Text>
      ) : null}
    </Stack>
  )
}

/** Read-only lifecycle line: status badge + who decided what, when. */
// eslint-disable-next-line react-refresh/only-export-components -- local component; the descriptor below is this file's only export
function StatusMeta({ rest }: { rest: PlanRest }) {
  const status = rest.status ?? 'submitted'
  return (
    <Inline gap="space-12">
      <StatusBadge tone={toneFor(status)}>{status}</StatusBadge>
      {rest.submitted_at ? (
        <Text variant="caption" tone="text-mute">
          {resourcesCopy.forms.plan.submitted(fmt(rest.submitted_at))}
        </Text>
      ) : null}
      {rest.decided_at ? (
        <Text variant="caption" tone="text-mute">
          {resourcesCopy.forms.plan.decided(
            status,
            fmt(rest.decided_at),
            rest.decided_by,
          )}
        </Text>
      ) : null}
    </Inline>
  )
}

/** A rejected plan asks the agent to act: warning, as submitted (D193). */
function toneFor(status: string): StatusTone {
  if (status === 'approved') return 'success'
  if (status === 'submitted' || status === 'rejected') return 'warning'
  return 'neutral'
}

function fmt(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export const planForm: ResourceFormDescriptor = {
  Fields: PlanFields,
  fromResource: (d: unknown) => {
    const { plan_text, ...rest } = (d ?? {}) as { plan_text?: string } & Record<
      string,
      unknown
    >
    return {
      plan_text: plan_text ?? '',
      [REST_KEY]: JSON.stringify(rest),
    }
  },
  toBody: (f: Fields) => ({
    ...parseRest(f[REST_KEY]),
    plan_text: f.plan_text ?? '',
  }),
}
